import {
  CorePagination,
  CoreSearch
} from '@komodo-design-system/komodo-components-react';
import Table from 'componentsV2/Table/Table';
import React, { useState } from 'react';
import Loading from 'components/Placeholders/Loading';
import './JobDetails.scss';
import { REFRESH_BUTTON_TITLE } from 'constants/constants';
import { dateFormatter } from 'utils/utils';
import { useJobDetails } from './useJobDetails';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import StatusFilter from './StatusFilter/StatusFilter';
import ActionPopUp from 'containers/HomePageV2/ActionPopUp';
import Actions from 'componentsV2/Shared/Actions';
import { BASE_URL } from 'constants/routes';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';

const JobDetails = () => {
  const {
    pageNumber,
    rpp,
    sorting,
    searchInput,
    filteredResults,
    isLoading,
    error,
    rppList,
    alert,
    setAlert,
    selectedStatus,
    statusStats,
    handleStatusClick,
    handleClearAllFilters,
    
    // Handlers
    searchHandler,
    handleRefreshClick,
    handleSortingChange,
    handleActivePageChange,
    searchItems,
    handleResultsPerPageChange,
    handleClearSearch,
    fetchJobDetails
  } = useJobDetails();

  const [actionPopUp, setActionPopUp] = useState({
    isOpen: false,
    inputProps: {},
    iconRef: "" 
  });
  const [actions, setActions] = useState<{
      operate: boolean;
      action: string;
      inputProps: any;
      handleActionResponse: (response: any) => void;
        }>({
          operate: false,
          action: '',
          inputProps: {},
          handleActionResponse: () => {}
        });
  const [responseAlert, setResponseAlert] = useState({ isOpen: false, message: '' });
  const navigate = useNavigate();
  const loading = useSelector((state: any) => state.loading.isLoading);
  

  const handleAssetDetail = (type: any, accessor: any, row: any) => {
    navigate(`${BASE_URL}/asset-detail/${row?.assetId}`,{
      state: { from: `${BASE_URL}/job-details/${row?.bulkUploadId}` }

    });
    window.scrollTo(0, 0);
  };
        
  const columns = [
    {
      header: 'Asset ID',
      accessor: 'assetId',
      sortable: true,
      onCellClick: (type: any, accessor: any, row: any) => {
        if (row?.assetId) {
          handleAssetDetail(type, accessor, row);
        }
      },
      cell: (row: any) => <span>{row?.assetId}</span>
    },
    {
      header: 'Asset Name',
      accessor: 'assetName',
      sortable: true,
      cell: (row: any) => (
        <span 
          style={{
            display: 'block',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={row?.assetName}
        >
          {row?.assetName}
        </span>
      )
      // cell: (row: any) => <span>{row?.assetName}</span>
    },
    {
      header: 'Start Date',
      accessor: 'createdDate',
      sortable: true,
      cell: (row: any) => <span>{dateFormatter(row.createdDate)}</span>
    },
    {
      header: 'Status',
      accessor: 'status',
      sortable: true,
      cell: (row: any) => {
        const status = row?.status?.toLowerCase() || 'failed';
        const formattedStatus = status.replaceAll('_', ' ');
        return (
          <span style={{ padding: '6px', borderRadius: '15px', color: 'white' }} className={status}>
            {formattedStatus.charAt(0).toUpperCase() + formattedStatus.slice(1).toLowerCase() || 'Failed'}
          </span>
        );
      }
    },
    {
      header: 'Completed Date',
      accessor: 'updatedDate',
      sortable: true,
      cell: (row: any) => <span>{dateFormatter(row?.updatedDate)}</span>
    },
    {
      header: 'Failure Message',
      accessor: 'errorMessage',
      sortable: true,
      cell: (row: any) => (
        <span 
          style={{
            display: 'block',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={row?.errorMessage}
        >
          {row?.errorMessage}
        </span>
      )
    }
  ];

  const handleActionIcon = (action:string,row:any,index:number) => {
    setActionPopUp({
      isOpen: true,
      inputProps: { 
        ...row, 
        navigateBackTo: `${BASE_URL}/job-details/${row?.bulkUploadId}` // Add context for navigation
      },
      iconRef: `action-icon-${action}-${index}`,
    });

  };

  const getActionIcons = (row?: any) => {
    // Only show action icons if assetId is available
    if (!row?.assetId) {
      return [];
    }
    
    return [
      {
        icon: 'icon-16-moreVert-bold',
        name: 'actions',
        tooltip: 'Actions',
        onClick: (action:string,rowData:any,index:number) => handleActionIcon(action,rowData, index),
      },
    ];
  };

  const handleActionResponse = (response: any,action:string,inputProps:any) => {
    setActions({ operate: false, action: '', inputProps: {}, handleActionResponse: () => {} }); // Reset actions state
    if (response?.status?.toString() === '200') {
      setResponseAlert({
        isOpen: true,
        message: `Asset ${action} successfully.`,
      });
    } else {
      setResponseAlert({
        isOpen: true,
        message: `Failed to ${action} asset.`,
      });
    }
  };

  const handleAction = (action: string, inputProps?: any) => {
    setActionPopUp({isOpen: false, inputProps: {},iconRef:''}); // Close the popup after action
    setActions({
      operate: true,
      action,
      inputProps,
      handleActionResponse: (response) => handleActionResponse(response, action, inputProps)
    });
  };

  const handleCloseAlert = () => {
    setResponseAlert({ isOpen: false, message: '' });
    fetchJobDetails();
  };


  if (error) {
    return <div>Error: {error}</div>;
  }


  return (
    <div className="job-detail-view">
      <div className="title-container">
        <div className="page-title">Job Details</div>
        <span
          className="icon-24-refresh-bold"
          onClick={handleRefreshClick}
          title={REFRESH_BUTTON_TITLE}
        />
      </div>
      
      <div className="search_block">
        <div className="search-filter-container">
          <div className="search-section">
            <div className="search-wrapper">
              <CoreSearch
                id="coreSearch"
                placeholder="Search by Asset ID, Asset Name or Status (Min.3 characters)"
                title="Search by Asset ID, Asset Name or Status (Min.3 characters)"
                onSearchCleared={handleClearSearch}
                onSearchQueryChanged={(e) => searchItems(e.detail?.trim())}
                onSearchEnterPress={() => searchHandler()}
                clearicon
                bold
              />
              {filteredResults?.records?.length === 0 && searchInput.length > 0 && (
                <div className="search-results">
                  <span className="errorMsg">
                    <span className="icon-16-warning-mono"></span>
                    Sorry, no matching record found
                  </span>
                </div>
              )}
              {filteredResults?.records?.length === 0 && searchInput.length === 0 && (
                <div style={{ paddingTop: '10px' }}>
                  No data available
                </div>
              )}
            </div>
          </div>
          
          {/* Status Filter positioned on the right side of search */}
          {statusStats.length > 0 && (
            <div className="filter-section">
              <div className="filter-wrapper">
                <StatusFilter
                  statusStats={statusStats}
                  selectedStatus={selectedStatus}
                  onStatusClick={handleStatusClick}
                  onClearAll={handleClearAllFilters}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {alert.open && <AlertBox
        message={alert.message}
        isModalOpen = {alert.open}
        handleClose={()=>setAlert({open: false, message: ''})}
      />}
      
      {(isLoading || loading) ? (
        <Loading />
      ) : (
        filteredResults?.records?.length > 0 && (
          <>
            <Table
              sorting={sorting}
              data={filteredResults?.records || []}
              columns={columns}
              onSortingChange={handleSortingChange}
              actionIcons={getActionIcons}
            />
            {filteredResults?.totalRecords > 2 && (
              <CorePagination
                variants={{ dropdown: true, indicator: false, goto: false }}
                resultsperpage={rppList}
                totalresults={filteredResults?.totalRecords}
                activepage={pageNumber || 1}
                activeresultsperpage={rpp}
                onResultsPerPageChange={(event) => handleResultsPerPageChange(event.detail)}
                onActivePageChange={(event) => handleActivePageChange(event.detail.activePage)}
              />
            )}
          </>
        )
      )}
      {actionPopUp?.isOpen && <ActionPopUp action={actionPopUp} handleAction={handleAction}  />}
      {actions.operate && <Actions action={actions?.action} inputProps={actions?.inputProps} handleActionResponse={actions?.handleActionResponse} />}
      {responseAlert?.isOpen && <AlertBox isModalOpen message={responseAlert?.message} handleClose={handleCloseAlert} />}


    </div>
  );
};

export default JobDetails;
